import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { ANNUAL_DISCOUNT_PCT } from "../../lib/plans";
import { Menu, X, ArrowRight, Plus } from "lucide-react";

export const L = (lang, fr, en) => (lang === "fr" ? fr : en);

// Square "CO" brand tile + wordmark, reused in nav + footer.
export function BrandMark({ compact }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="marketing-brand">
      <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 shadow-sm shadow-blue-600/30">
        <span className="font-heading font-black text-white text-sm tracking-tight">CO</span>
      </div>
      {!compact && (
        <div className="leading-none">
          <div className="font-heading font-bold text-slate-900 text-[15px]">Cockpit OS</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-0.5">Operating System</div>
        </div>
      )}
    </div>
  );
}

export function MarketingNav() {
  const { lang, toggleLang, user } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [
    { href: "/#features", label: L(lang, "Fonctionnalités", "Features"), key: "features" },
    { to: "/pricing", label: L(lang, "Tarifs", "Pricing"), key: "pricing" },
    { href: "/blog", label: "Blog", key: "blog" },
    { href: "/#faq", label: "FAQ", key: "faq" },
  ];
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl" data-testid="marketing-nav">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><BrandMark /></Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => l.to ? (
            <Link key={l.key} to={l.to} className="text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid={`nav-link-${l.key}`}>{l.label}</Link>
          ) : (
            <a key={l.key} href={l.href} className="text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid={`nav-link-${l.key}`}>{l.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={toggleLang} data-testid="nav-language-toggle"
            className="px-2.5 py-1.5 rounded-md text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">
            {lang === "fr" ? "EN" : "FR"}
          </button>
          {user ? (
            <button onClick={() => navigate("/")} data-testid="nav-open-app"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
              {L(lang, "Ouvrir l'app", "Open app")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <>
              <button onClick={() => navigate("/login")} data-testid="nav-login"
                className="hidden sm:inline-flex px-3.5 py-1.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors">
                {L(lang, "Connexion", "Log in")}
              </button>
              <button onClick={() => navigate("/login?signup=1")} data-testid="nav-get-started"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
                {L(lang, "Commencer", "Get started")}
              </button>
            </>
          )}
          <button onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" className="md:hidden p-1.5 text-slate-900">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 px-5 py-3 space-y-2 bg-white" data-testid="nav-mobile-menu">
          {links.map((l) => l.to ? (
            <Link key={l.key} to={l.to} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-slate-600">{l.label}</Link>
          ) : (
            <a key={l.key} href={l.href} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-slate-600">{l.label}</a>
          ))}
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  const { lang } = useApp();
  const year = new Date().getFullYear();
  const cols = [
    { title: L(lang, "Produit", "Product"), items: [
      { label: L(lang, "Fonctionnalités", "Features"), href: "/#features" },
      { label: L(lang, "Tarifs", "Pricing"), to: "/pricing" },
      { label: "FAQ", href: "/#faq" },
    ] },
    { title: L(lang, "Entreprise", "Company"), items: [
      { label: L(lang, "À propos", "About"), href: "/#features" },
      { label: "Blog", href: "/blog" },
      { label: L(lang, "Contact", "Contact"), href: "mailto:hello@cockpit-os.com" },
    ] },
    { title: L(lang, "Légal", "Legal"), items: [
      { label: L(lang, "Confidentialité (RGPD)", "Privacy (GDPR)"), to: "/privacy" },
      { label: L(lang, "CGV", "Terms of Sale"), to: "/terms" },
      { label: L(lang, "Mentions légales", "Legal notice"), to: "/legal" },
    ] },
  ];
  return (
    <footer className="border-t border-slate-200 bg-slate-50" data-testid="marketing-footer">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-2">
          <BrandMark />
          <p className="text-sm text-slate-500 mt-4 max-w-xs leading-relaxed">
            {L(lang, "Le système d'exploitation pour votre travail, votre argent, vos actifs et vos décisions.",
                     "The operating system for your work, money, assets, and decisions.")}
          </p>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{c.title}</div>
            <ul className="space-y-2">
              {c.items.map((it) => (
                <li key={it.label}>
                  {it.to ? (
                    <Link to={it.to} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">{it.label}</Link>
                  ) : (
                    <a href={it.href} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">{it.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <span className="text-xs text-slate-400">© {year} Cockpit OS. {L(lang, "Tous droits réservés.", "All rights reserved.")}</span>
          <span className="text-xs text-slate-400 font-mono">EUR · FR / EN</span>
        </div>
      </div>
    </footer>
  );
}

// Lightweight accordion item used in marketing FAQ.
export function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-trigger`}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group">
        <span className="font-heading font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{q}</span>
        <Plus className={cn("w-5 h-5 shrink-0 text-slate-400 transition-transform duration-300", open && "rotate-45 text-blue-600")} />
      </button>
      {open && <p className="text-sm text-slate-600 leading-relaxed pb-5 -mt-1 max-w-2xl">{a}</p>}
    </div>
  );
}

// Monthly / Annual billing toggle — shared by Landing preview + Pricing page.
export function BillingToggle({ cycle, onChange, lang }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="billing-toggle">
      {[
        { id: "monthly", label: L(lang, "Mensuel", "Monthly") },
        { id: "annual", label: L(lang, "Annuel", "Annual") },
        { id: "lifetime", label: L(lang, "À vie", "Lifetime") },
      ].map((opt) => (
        <button key={opt.id} onClick={() => onChange(opt.id)} data-testid={`billing-toggle-${opt.id}`}
          className={cn(
            "px-4 py-1.5 rounded-full text-sm font-semibold transition-all",
            cycle === opt.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          )}>
          {opt.label}
          {opt.id === "annual" && (
            <span className="ml-1.5 text-[10px] font-bold text-emerald-600">−{ANNUAL_DISCOUNT_PCT}%</span>
          )}
          {opt.id === "lifetime" && (
            <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{L(lang, "Offre limitée", "Limited time")}</span>
          )}
        </button>
      ))}
    </div>
  );
}
